import type { PagesKeys } from '@common/enums/pages.enum'
import { getLogger } from '@omnia/ui/src/lib/services/logger/logger.service'
import { useOccConfig } from '@vista-digital/occ-ui'
import { runInAction } from 'mobx'
import type { FunctionComponent } from 'react'
import { memo, Suspense, useMemo } from 'react'
import { useLocation } from 'react-router-dom-v7'
import { useAbandonOrder } from '@/common/hooks/useAbandonOrder'
import { mobileEmbedService } from '@/content/services/mobileEmbed/mobileEmbed.service'
import { getCustomFilmActions } from '../content/components/occConfiguration/utils/getCustomFilmActions'
import { PageLoading } from '../content/pages/helpers/PageLoading'
import { Pages } from '../content/pages/pages'
import { navigationService } from '../content/services/navigation.service'

const logger = getLogger('App')
const allowedMobileEmbedPages = [
    'BookingConfirmation',
    'ReviewTicket',
    'Ticketing',
    'GiftStore',
    'Generic',
    'FoodAndBeverage',
    'FoodAndBeverageBookingConfirmation',
] as PagesKeys[]

export const PageWrapper: FunctionComponent<{ page: PagesKeys; overrideElement?: FunctionComponent }> = memo(props => {
    const occConfig = useOccConfig()
    const location = useLocation()
    const isMobileApp = mobileEmbedService.isMobileApp()

    // Run in useMemo instead of useEffect to ensure this is run during the render phase.
    useMemo(() => {
        runInAction(() => {
            const previousPage = navigationService.getCurrentPage()!

            logger.value.trace('Updating previous and current page', { previousPage, currentPage: props.page })

            // Close embed for mobile app if on unwanted pages
            if (isMobileApp) {
                if (!allowedMobileEmbedPages.includes(props.page)) {
                    logger.value.debug(`Page ${props.page} is not allowed in mobile embed, closing embed.`)
                    mobileEmbedService.close()
                }
            }

            navigationService.setPreviousPage(previousPage)
            navigationService.setCurrentPage(props.page)

            if (!navigationService.Pages[props.page].subPages) {
                navigationService.setCurrentSubPage(undefined)
            }

            if (doCustomFilmActionsNeedToBeSet(previousPage, props.page)) {
                // Initially load film actions using no site - a site id is only required for Cinema and Cinema Events pages, which override setting this data.
                void getCustomFilmActions(undefined).then(actions => {
                    logger.value.debug('Setting custom film actions', actions)
                    runInAction(() => {
                        occConfig.film.customActions = actions
                    })
                })
            }
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps -- only run on page change
    }, [location.key, props.page])

    useAbandonOrder()

    logger.value.debug('PageWrapper', props.page.toString(), Date.now() - window.loadMs)

    const PageElement = props?.overrideElement ?? Pages[props.page].component
    return (
        <Suspense fallback={<PageLoading />}>
            <PageElement />
        </Suspense>
    )
})

const doCustomFilmActionsNeedToBeSet = (previousPage: PagesKeys, currentPage: PagesKeys) => {
    const isInitialSiteLoad = previousPage === undefined
    const previousPageHadCustomFilmActions = previousPage === 'Cinema' || previousPage === 'CinemaEvents'
    const targetPageHasCustomFilmActions = currentPage === 'Cinema' || currentPage === 'CinemaEvents'

    // Always allow Cinema and Cinema Events pages to set their own custom film actions.
    if (targetPageHasCustomFilmActions) {
        return false
    }

    if (isInitialSiteLoad) {
        logger.value.debug(
            'Detected initial page load and not on a cinema page or a cinema events page. Loading default custom film actions.'
        )
        return true
    }

    if (previousPageHadCustomFilmActions) {
        logger.value.debug(
            'Detected leaving a cinema page or a cinema events page. Resetting custom film actions to defaults.'
        )
        return true
    }
}
